import { LockClosedIcon } from '@heroicons/react/24/outline'
import { useQueryClient } from '@tanstack/react-query'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import * as Yup from 'yup'

import { QUERY_KEYS } from 'constants/constants'
import Input from '../input'
import SelectMenu from '../select-menu'
// import SelectRiasecSearchInput from '../select-riasec-search-input'
import { useState } from 'react'
import { CREATE_CAREER, UPDATE_STATUS } from '../../constants/career-history'
import { DE, FR, NL, getLanguage } from '../../constants/languages'
import trackEvents from '../../constants/track-events'
import {
  useAivyContext,
  usePartnerContext,
  usePaymentContext,
  useProductTourContext
} from '../../context'
import { useLimitGuards } from '../../features/pricing/guards'
import { invalidatePricingUsage } from '../../features/pricing/hooks'
import { LIMIT_DIALOG_ACTIONS } from '../../features/pricing/pricing-dialog-context'
import { replacesCountedCareer } from '../../features/pricing/usage/careers'
import { useFeatureGate } from '../../features/pricing/use-feature-gate'
import { mutation, query } from '../../graphql'
import { DIAGNOSTIC_VERSION, sendSlackMessage } from '../../helpers'
import { trackEvent } from '../../helpers/analytics'
import { getAssessmentLanguageDescription } from '../../helpers/assessment-language-description'
import { getCreateCareerInput } from '../../helpers/career'
import {
  ASSESSMENT_LANGUAGE_FEATURES,
  getLockedAssessmentLanguages,
  getSelectableAssessmentLanguages
} from '../../helpers/mail-template-language'
import { useCreateLog } from '../../hooks/use-create-log'
import { useMount } from '../../hooks/use-mount'
import LockBadge from '../pricing/lock-badge'
import Button from '../tailwind/Button'
import InfoCallout from '../tailwind/InfoCallout'
import CreateCareerFooter from './CreateCareerFooter'
import CareerLimitHint from './career-limit-hint'
import ExternalIdAlert from './pieces/ExternalIdAlert'

const I18N_LOCATION = 'components.careers.create_career_dialog.'

const CreateNewCareer = ({ career, careerMutation, isDuplicate, careers }) => {
  const { t } = useTranslation()
  const { id: career_id_param } = useParams()
  const { is_trial } = usePaymentContext()

  const { partner } = usePartnerContext()
  const { system } = useAivyContext()

  const {
    setProductTourState,
    productTourState: { tourActive }
  } = useProductTourContext()

  useMount(() => {
    if (!career_id_param) {
      trackEvent(trackEvents.CREATE_CAREER_STEP_1_VIEWED)
    }

    if (tourActive) {
      setTimeout(() => {
        setProductTourState({ run: true })
      }, 0.4 * 1000)
    }
  })

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { createCareerHistoryLog } = useCreateLog()

  const [isLoadingNext, setIsLoadingNext] = useState(false)

  const { guardCareerActivation } = useLimitGuards()

  // FR und NL sind nur in enforced gesperrt, sonst ist locked false und die
  // Auswahl bleibt wie heute
  const frGate = useFeatureGate('LANGUAGE_FR', {
    source: 'create_career_language'
  })
  const nlGate = useFeatureGate('LANGUAGE_NL', {
    source: 'create_career_language'
  })
  const languageGates = { [FR]: frGate, [NL]: nlGate }

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      title: career.title || '',
      category: career.category || '-1',
      language: career.language || DE,
      external_custom_id: career.external_custom_id || ''
    },
    validationSchema: Yup.object({
      title: Yup.string().required(
        t(I18N_LOCATION + 'input_career_title_validation')
      ),
      external_custom_id: Yup.string().matches(
        /^[a-zA-Z0-9-]+$/,
        t(I18N_LOCATION + 'input_external_custom_id_validation')
      )
    })
  })

  const languageOptions = getSelectableAssessmentLanguages({
    system,
    currentLanguage: formik.initialValues.language
  })

  // Ein Entwurf behält seine gespeicherte Sprache. Ein Duplikat ist eine neue
  // Stelle und bekommt keine Ausnahme, auch wenn es die Sprache der Vorlage
  // mitbringt.
  const lockedLanguages = getLockedAssessmentLanguages({
    fr: frGate.locked,
    nl: nlGate.locked,
    currentLanguage: isDuplicate ? undefined : formik.initialValues.language,
    options: languageOptions
  })

  // Bringt ein Duplikat eine gesperrte Sprache mit, ist sie gewählt und
  // SelectMenu zeigt an ihr keine Pille. Der Hinweis darunter macht die
  // Sperre vor dem Aktivieren sichtbar.
  const lockedLanguageFeature = lockedLanguages.includes(formik.values.language)
    ? ASSESSMENT_LANGUAGE_FEATURES[formik.values.language]
    : null

  const handleSubmit = async ({
    title,
    category,
    language,
    external_custom_id
  }) => {
    // Seite, kein Modal, der Upgrade-Dialog darf direkt öffnen. Erreichbar
    // nur mit einem Duplikat, dessen Vorlage eine gesperrte Sprache hat.
    if (lockedLanguages.includes(language)) {
      languageGates[language]?.open()
      return
    }

    formik.setSubmitting(true)

    if (is_trial) {
      queryClient.refetchQueries({
        queryKey: QUERY_KEYS.trialDataByPartner(partner.id),
        type: 'active'
      })
    }

    if (!career_id_param) {
      // create a new career
      try {
        const new_career = await mutation({
          mutation: 'createCareer',
          input: {
            external_custom_id: external_custom_id || undefined,
            diagnostic_version: DIAGNOSTIC_VERSION,
            ...getCreateCareerInput({
              title,
              category,
              language,
              partner_id: partner.id,
              status: 'WAIT_CULTURAL_FIT'
            })
          }
        })

        await createCareerHistoryLog({
          career_id: new_career.id,
          context: CREATE_CAREER,
          input: { status: 'WAIT_CULTURAL_FIT' }
        })

        navigate(`/career/new/${new_career.id}`, { replace: true })
      } catch (err) {
        formik.setSubmitting(false)
      }
    } else {
      // update the career or activate if is duplicate
      if (isDuplicate) {
        setIsLoadingNext(true)

        // Die Kopie wird hier direkt aktiv. Ist kein Platz frei, öffnet der
        // Guard den Limit-Dialog, und die Kopie bleibt als Entwurf erhalten.
        // Ersetzt sie per external_custom_id eine aktive Stelle, kostet das
        // keinen Platz.
        const allowed = await guardCareerActivation(
          { ...career, external_custom_id: external_custom_id || null },
          {
            source: 'create_career_duplicate',
            action: LIMIT_DIALOG_ACTIONS.DUPLICATE
          }
        )
        if (!allowed) {
          setIsLoadingNext(false)
          formik.setSubmitting(false)
          return
        }

        await createCareerHistoryLog({
          career_id: career.id,
          context: UPDATE_STATUS,
          input: { status: 'ACTIVE' }
        })

        await mutation({
          mutation: 'updateCareer',
          input: {
            id: career.id,
            title,
            // category,
            language,
            // if external_custom_id is an empty string pass null
            // DynamoDB treats null as a removal operation for that attribute
            external_custom_id: external_custom_id || null
          }
        })

        const response = await query({
          query: 'careerControl',
          variables: { action: 'activateCareer', career_id: career.id }
        })

        const { name: partner_name } = partner
        const { id: activated_career_id, title: career_title } =
          JSON.parse(response).activateCareer.res

        if (!activated_career_id.includes('sample')) {
          trackEvent(trackEvents.FINISH_CREATE_CAREER)

          if (is_trial) {
            queryClient.refetchQueries({
              queryKey: QUERY_KEYS.trialDataByPartner(partner.id),
              type: 'active'
            })
          }

          sendSlackMessage(
            'kunden-controlling',
            `Die Stelle *${career_title}* (${activated_career_id}) von _${partner_name}_ ${
              is_trial ? '(TRIAL)' : ''
            } ist nun aktiv.`
          )
        }

        // Die Kopie belegt jetzt einen Platz, Banner und Meter zählen neu
        invalidatePricingUsage({
          queryClient,
          partnerId: partner.id,
          resource: 'careers'
        })

        // invalidate cache because career_id has changed
        if (activated_career_id !== career.id) queryClient.removeQueries()

        setIsLoadingNext(false)
        navigate(`/career/${activated_career_id}`, { replace: true })

        return
      }

      careerMutation.mutate(
        {
          input: {
            id: career.id,
            title,
            category,
            language,
            // if external_custom_id is an empty string pass null
            // DynamoDB treats null as a removal operation for that attribute
            external_custom_id: external_custom_id || null,
            status: 'WAIT_CULTURAL_FIT'
          }
        },
        {
          onSuccess: (data) => {
            createCareerHistoryLog({
              career_id: data.id,
              context: UPDATE_STATUS,
              input: { status: 'WAIT_CULTURAL_FIT' }
            })
          },
          onError: () => formik.setSubmitting(false)
        }
      )
    }
  }

  const foundCareerWithExternalCustomID = careers.find(
    ({ status, external_custom_id }) => {
      return (
        status === 'ACTIVE' &&
        external_custom_id === formik.values.external_custom_id
      )
    }
  )

  return (
    <div className='mx-auto max-w-5xl'>
      <h1 className='text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl'>
        {t(I18N_LOCATION + 'dialog_title')}
      </h1>
      <p className='mt-3 text-base text-gray-600'>
        {t(I18N_LOCATION + 'dialog_description')}
      </p>
      {/* Weich, ein Entwurf geht immer. Das Duplikat prüft der Guard beim
          Aktivieren. Ersetzt wird nur eine gezählte Stelle, dieselbe Regel
          wie im Guard. */}
      {!isDuplicate && (
        <CareerLimitHint
          replacesActiveCareer={replacesCountedCareer({
            career: {
              external_custom_id: formik.values.external_custom_id || null,
              partner_id: partner.id
            },
            careers
          })}
          source='create_career'
          className='mt-6'
        />
      )}
      <form className='mt-2'>
        <div className='mt-6'>
          <label htmlFor='title' className='sr-only'>
            {t(I18N_LOCATION + 'input_career_title_label_sronly')}
          </label>
          <Input
            id='title'
            label={t(I18N_LOCATION + 'input_career_title_label')}
            placeholder={t(I18N_LOCATION + 'input_career_title_placeholder')}
            onChange={formik.handleChange}
            value={formik.values.title}
            error={formik.errors.title}
            touched={formik.touched.title}
            onBlur={formik.handleBlur}
          />
        </div>
        <div className='relative mt-6'>
          <label htmlFor='title' className='sr-only'>
            {t(I18N_LOCATION + 'input_external_custom_id_label_sronly')}
          </label>
          <Input
            id='external_custom_id'
            label={t(I18N_LOCATION + 'input_external_custom_id_label')}
            placeholder={t(
              I18N_LOCATION + 'input_external_custom_id_placeholder'
            )}
            description={t(
              I18N_LOCATION + 'input_external_custom_id_description'
            )}
            onChange={formik.handleChange}
            value={formik.values.external_custom_id}
            error={formik.errors.external_custom_id}
            touched={formik.touched.external_custom_id}
            onBlur={formik.handleBlur}
          />
          {foundCareerWithExternalCustomID && (
            <ExternalIdAlert
              className='mt-2'
              old_career_title={foundCareerWithExternalCustomID.title}
              new_career_title={formik.values.title || 'n/a'}
              external_custom_id={formik.values.external_custom_id}
            />
          )}
        </div>
        <div id='product-tour-language' className='mt-6'>
          <SelectMenu
            id='language'
            label={t(I18N_LOCATION + 'language_label')}
            options={languageOptions}
            defaultValue={formik.values.language}
            onChange={(language) => formik.setFieldValue('language', language)}
            lockedOptions={lockedLanguages}
            onLockedSelect={(language) => languageGates[language]?.open()}
            description={getAssessmentLanguageDescription({ t })}
          />
          {lockedLanguageFeature && (
            <InfoCallout
              tone='neutral'
              icon={LockClosedIcon}
              className='mt-3'
              action={
                <LockBadge
                  feature={lockedLanguageFeature}
                  source='create_career_language'
                />
              }
            >
              {t('pricing.gates.language_duplicate_hint', {
                language: t(getLanguage(formik.values.language))
              })}
            </InfoCallout>
          )}
        </div>
        {/* <div className='mt-6'>
          <SelectMenu
            id='career_category'
            label={t(I18N_LOCATION + 'category_label')}
            options={getAllJobCategories()}
            defaultValue={formik.values.category}
            onChange={(category) => formik.setFieldValue('category', category)}
            position='auto'
          />
        </div> */}
        <CreateCareerFooter>
          <div className='flex justify-end gap-x-2'>
            <Button.SecondaryXL
              onClick={() => {
                trackEvent(trackEvents.ABORT_CREATE_CAREER)
                navigate('/careers')
              }}
              text={t('create_career.cancel_action')}
            />
            <Button.PrimaryXL
              text={
                isDuplicate
                  ? t('create_career.overview.submit_action')
                  : t('create_career.next_action')
              }
              onClick={() => handleSubmit(formik.values)}
              disabled={!formik.isValid}
              isLoading={formik.isSubmitting || isLoadingNext}
              className='bg-green-600 hover:bg-green-500'
            />
          </div>
        </CreateCareerFooter>
      </form>
    </div>
  )
}

CreateNewCareer.propTypes = {
  career: PropTypes.object.isRequired,
  careers: PropTypes.array.isRequired,
  careerMutation: PropTypes.object.isRequired,
  isDuplicate: PropTypes.bool.isRequired
}

export default CreateNewCareer
